/**
 * Synthesised engine + crash sounds on the Web Audio API (design.md B9):
 * no sample files to download. Browsers only allow audio after a user
 * gesture, so `unlock()` is called from the first key/tap/button.
 */
export class EngineAudio {
  private ctx: AudioContext | null = null;
  private master: GainNode | null = null;
  private oscA: OscillatorNode | null = null;
  private oscB: OscillatorNode | null = null;
  private filter: BiquadFilterNode | null = null;
  private engineGain: GainNode | null = null;
  muted = false;

  constructor() {
    try {
      this.muted = localStorage.getItem('sms.muted') === '1';
    } catch {
      /* storage unavailable */
    }
  }

  unlock(): void {
    if (this.ctx) {
      if (this.ctx.state === 'suspended') void this.ctx.resume();
      return;
    }
    const AC = window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
    if (!AC) return;
    const ctx = new AC();
    this.ctx = ctx;
    this.master = ctx.createGain();
    this.master.gain.value = this.muted ? 0 : 0.5;
    this.master.connect(ctx.destination);

    this.filter = ctx.createBiquadFilter();
    this.filter.type = 'lowpass';
    this.filter.frequency.value = 900;
    this.filter.Q.value = 4;
    this.engineGain = ctx.createGain();
    this.engineGain.gain.value = 0;
    this.filter.connect(this.engineGain).connect(this.master);

    this.oscA = ctx.createOscillator();
    this.oscA.type = 'sawtooth';
    this.oscB = ctx.createOscillator();
    this.oscB.type = 'square';
    this.oscB.detune.value = -1200 + 7;
    const gb = ctx.createGain();
    gb.gain.value = 0.35;
    this.oscA.connect(this.filter);
    this.oscB.connect(gb).connect(this.filter);
    this.oscA.start();
    this.oscB.start();
  }

  setMuted(m: boolean): void {
    this.muted = m;
    try {
      localStorage.setItem('sms.muted', m ? '1' : '0');
    } catch {
      /* ignore */
    }
    if (this.master && this.ctx) this.master.gain.setTargetAtTime(m ? 0 : 0.5, this.ctx.currentTime, 0.05);
  }

  /** speedRatio 0…1 of top speed; throttle 0…1; running false = silent. */
  update(speedRatio: number, throttle: number, running: boolean): void {
    if (!this.ctx || !this.oscA || !this.oscB || !this.filter || !this.engineGain) return;
    const t = this.ctx.currentTime;
    // Fake gearbox: rpm climbs through 6 gears.
    const gears = 6;
    const g = Math.min(gears - 1, Math.floor(speedRatio * gears));
    const inGear = speedRatio * gears - g;
    const rpm = 0.35 + 0.65 * inGear;
    const f = 48 + rpm * (110 + g * 14) + throttle * 8;
    this.oscA.frequency.setTargetAtTime(f, t, 0.03);
    this.oscB.frequency.setTargetAtTime(f, t, 0.03);
    this.filter.frequency.setTargetAtTime(500 + rpm * 1400 + throttle * 600, t, 0.05);
    const vol = running ? 0.05 + 0.1 * throttle + 0.05 * speedRatio : 0;
    this.engineGain.gain.setTargetAtTime(vol, t, 0.06);
  }

  crash(): void {
    if (!this.ctx || !this.master) return;
    const ctx = this.ctx;
    const len = Math.floor(ctx.sampleRate * 0.7);
    const buf = ctx.createBuffer(1, len, ctx.sampleRate);
    const d = buf.getChannelData(0);
    for (let i = 0; i < len; i++) d[i] = (Math.random() * 2 - 1) * Math.pow(1 - i / len, 2.2);
    const src = ctx.createBufferSource();
    src.buffer = buf;
    const lp = ctx.createBiquadFilter();
    lp.type = 'lowpass';
    lp.frequency.value = 1800;
    const g = ctx.createGain();
    g.gain.value = 0.9;
    src.connect(lp).connect(g).connect(this.master);
    src.start();
  }

  blip(freq = 880, dur = 0.09): void {
    if (!this.ctx || !this.master) return;
    const o = this.ctx.createOscillator();
    o.type = 'square';
    o.frequency.value = freq;
    const g = this.ctx.createGain();
    const t = this.ctx.currentTime;
    g.gain.setValueAtTime(0.12, t);
    g.gain.exponentialRampToValueAtTime(0.001, t + dur);
    o.connect(g).connect(this.master);
    o.start(t);
    o.stop(t + dur);
  }
}
